"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.siblingsSummary = exports.childrenSummary = exports.renderDescriptor = exports.classKey = exports.describeOffspring = exports.token = exports.shortLabel = void 0;
const textNorm_1 = require("./textNorm");
const tokenEscape_1 = require("./tokenEscape");
// Descriptors here stay structural: counts and text shapes over the view tree,
// never semantic inference (no "title"/"table"/"header" classification).
// Strip trailing (_<digits>)+ from label/description text (e.g. a
// data-binding index baked into a listing entry: `poi_hot_city_0_0` ->
// `poi_hot_city`), so otherwise-identical repeated list items normalize to
// the same short-listing entry instead of one per numeric suffix.
const NUMERIC_SUFFIX_RE = /(_\d+)+$/;
// Fallback for the callers that take no `EnrichmentConfig`; must stay equal to
// the production `enrichShortMaxChars` or the same node classifies differently
// depending on which entry point reached it.
const SHORT_MAX_CHARS = 30;
/**
 * Reuses `nodeText` and `shape` — the same classification leaf normalization
 * applies — so a node's offspring token matches how it would be described as
 * the element itself.
 */
const shortLabel = (node, shortMax = SHORT_MAX_CHARS) => {
    const text = (0, textNorm_1.nodeText)(node);
    return (0, textNorm_1.shape)(text, shortMax) === 'Short' ? text : '';
};
exports.shortLabel = shortLabel;
/**
 * `className:shortLabel` token for a single node. The class is normalized with
 * the same normalizer and field name `generateViewSelector` uses
 * (`selector.ts`), so an offspring descriptor's classes match how those nodes
 * would appear if they were the element themselves.
 */
const token = (node, shortMax = SHORT_MAX_CHARS, normalizer = null) => {
    const raw = node.className || '';
    const className = normalizer !== null && raw
        ? normalizer.normalize(raw, 'className')
        : raw;
    return `${(0, tokenEscape_1.escapeEnrichmentValue)(className)}:${(0, tokenEscape_1.escapeEnrichmentValue)((0, exports.shortLabel)(node, shortMax))}`;
};
exports.token = token;
/**
 * BFS descriptor of `view`'s children down to `depth` levels. Tells apart
 * elements whose own selector collides — a repeated icon button distinguished
 * only by what it contains.
 *
 * `depth=1` -> direct children only. Each level's tokens are sorted (stable,
 * position-independent) and joined with `,`; an empty level becomes
 * `EMPTY_MARKER` ("∅"). Levels are joined with `|`.
 *
 * A level whose node count exceeds `maxNodes` is capped: only the first
 * `maxNodes` nodes in traversal order are rendered, plus a trailing `…+N`
 * token for how many were dropped. Those kept nodes are also the only ones
 * whose children feed the next level — the cap bounds the subtree walk
 * itself, not merely the printed line.
 */
const describeOffspring = ({ view, getChildren, depth, maxNodes, shortMax = SHORT_MAX_CHARS, normalizer = null }) => {
    const levels = [];
    let current = [...getChildren(view)];
    for (let i = 0; i < depth; i++) {
        // Which nodes survive a cap is document-order dependent, unlike the sorted
        // output - accepted, because a capped level is by construction an outlier.
        const total = current.length;
        const kept = total > maxNodes ? current.slice(0, maxNodes) : current;
        const tokens = kept.map(n => (0, exports.token)(n, shortMax, normalizer)).sort();
        if (total > kept.length) {
            tokens.push(`…+${total - kept.length}`);
        }
        levels.push(tokens.length ? tokens.join(',') : textNorm_1.EMPTY_MARKER);
        current = kept.flatMap(n => getChildren(n));
    }
    return levels.join('|');
};
exports.describeOffspring = describeOffspring;
/**
 * The unit `renderDescriptor`'s `cls:` histogram counts. Class and role are
 * joined rather than counted separately because one class serves several roles
 * — two `AccessibilityNode` children are a weaker signal than one `/control`
 * and one `/textElement`.
 *
 * Applies the same field normalization as the element selector when a
 * `normalizer` is supplied, so a class shown in Siblings/Children matches how
 * it appears in `Element:`.
 */
const classKey = (view, normalizer = null) => {
    const rawCls = view.className || '';
    const cp = (view.calculatedProps || {});
    // Chained falsy-fallthrough (role -> calculatedRole -> "?"): an empty
    // string must fall through to the next source, hence || (not ??).
    const rawRole = cp.role || view.calculatedRole || '?';
    let cls;
    let role;
    if (normalizer !== null) {
        cls = rawCls
            ? normalizer.normalize(rawCls, 'className') || '?'
            : '?';
        role = normalizer.normalize(rawRole, 'role') || '?';
    }
    else {
        cls = rawCls.split('.').pop() || '?';
        role = rawRole;
    }
    return `${cls}/${role}`;
};
exports.classKey = classKey;
/**
 * The separator between key and count is U+00D7 MULTIPLICATION SIGN — an
 * "x"-look-alike, but NOT the ASCII letter "x".
 */
const hist = (items, topk) => {
    const counts = new Map();
    for (const item of items) {
        counts.set(item, (counts.get(item) ?? 0) + 1);
    }
    let ordered = [...counts.entries()].sort(([keyA, countA], [keyB, countB]) => {
        if (countA !== countB) {
            return countB - countA;
        }
        // `counts` is a Map, so its keys are unique and `sort` never compares an
        // entry with itself - `keyA === keyB` cannot occur.
        return keyA < keyB ? -1 : 1;
    });
    if (topk !== null) {
        ordered = ordered.slice(0, topk);
    }
    return ordered.map(([k, n]) => `${(0, tokenEscape_1.escapeEnrichmentValue)(k)}×${n}`).join(',');
};
/**
 * Structural descriptor for a list of sibling/child views: a class histogram,
 * a shape histogram, and a short-text listing.
 *
 * Two views can differ in exact text yet be structurally identical (two rows
 * showing different dates, two buttons with different short labels), so the
 * list is also characterized by text SHAPE rather than raw text: a
 * `shape:Date×3` entry captures "three date-like children" without caring
 * which dates, giving templated UI a descriptor stable across instances.
 */
const renderDescriptor = (views, config, normalizer = null) => {
    if (views.length === 0) {
        return textNorm_1.EMPTY_MARKER;
    }
    const shortMax = config.enrichShortMaxChars;
    const texts = views.map(textNorm_1.nodeText);
    const shapes = texts.map(t => (0, textNorm_1.shape)(t, shortMax));
    const clsHist = hist(views.map(v => (0, exports.classKey)(v, normalizer)), config.enrichClassTopk);
    const shpHist = hist(shapes, null);
    const parts = [`cls:${clsHist}`, `shape:${shpHist}`];
    let shorts = texts
        .filter((_t, i) => shapes[i] === 'Short')
        .map(t => Array.from(t).slice(0, shortMax).join(''))
        .slice(0, config.enrichShortListing);
    if (shorts.length > 0) {
        shorts = shorts.map(s => s.replace(NUMERIC_SUFFIX_RE, ''));
        parts.push(`short:[${shorts.map(tokenEscape_1.escapeEnrichmentValue).join('|')}]`);
    }
    return parts.join('; ');
};
exports.renderDescriptor = renderDescriptor;
/**
 * The fingerprint's `Children:` line. Describes the node's own child list, or
 * its whole subtree down to `offspringDepth` levels once that is 2 or more.
 *
 * The numeric-suffix strip `renderDescriptor` applies does not reach here:
 * it exists to keep the lines that restate the element (`Siblings:`, and
 * `Ancestry:` under a generic parent) agreeing with the stripped `Element:`
 * line, and `Children:` never contains the node itself.
 */
const childrenSummary = (tree, viewId, config, normalizer = null) => {
    if (config.offspringDepth >= 2) {
        const leaf = tree.getView(viewId);
        if (!leaf) {
            return textNorm_1.EMPTY_MARKER;
        }
        return (0, exports.describeOffspring)({
            view: leaf,
            getChildren: vd => tree.getChildren(String(vd.axeViewId || '')),
            depth: config.offspringDepth,
            maxNodes: config.offspringMaxNodes,
            shortMax: config.enrichShortMaxChars,
            normalizer
        });
    }
    return (0, exports.renderDescriptor)(tree.getChildren(viewId), config, normalizer);
};
exports.childrenSummary = childrenSummary;
/**
 * The fingerprint's `Siblings:` line. Describes the parent's full child list,
 * `viewId` itself included. `EMPTY_MARKER` when `viewId` has no parent.
 */
const siblingsSummary = (tree, viewId, config, normalizer = null, cache) => {
    const parent = tree.getParent(viewId);
    if (parent === undefined) {
        return textNorm_1.EMPTY_MARKER;
    }
    const parentId = String(parent.axeViewId || '');
    const hit = cache?.get(parentId);
    if (hit !== undefined) {
        return hit;
    }
    const result = (0, exports.renderDescriptor)(tree.getChildren(parentId), config, normalizer);
    cache?.set(parentId, result);
    return result;
};
exports.siblingsSummary = siblingsSummary;
//# sourceMappingURL=enrichment.js.map